import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
  Server, Users, Globe, Lock, AlertCircle, Clock, Wifi, WifiOff,
  RefreshCw, Power, Square, RotateCcw, Zap, Cpu, MemoryStick, HardDrive,
  Copy, Check, ChevronDown, ChevronUp, Map, Pencil, X, Terminal, Send, Eye, EyeOff
} from 'lucide-react'
import { gameServersAPI, nasAPI, authAPI, pterodactylAPI } from '@/lib/api'
import api from '@/lib/api'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'

const UT2004_MAPS = {
  'Deathmatch': [
    'DM-Antalus','DM-Asbestos','DM-Compressed','DM-Corrugation','DM-Curse4',
    'DM-Deck17','DM-Gael','DM-Gestalt','DM-Goliath','DM-HyperBlast2',
    'DM-Icetomb','DM-Inferno','DM-Injector','DM-Insidious','DM-IronDeity',
    'DM-Junkyard','DM-Leviathan','DM-Metallurgy','DM-Morpheus3','DM-Oceanic',
    'DM-Phobos2','DM-Plunge','DM-Rankin','DM-Rrajigar','DM-Satellite',
    'DM-Severance','DM-Shangri-La','DM-Shrapnel4','DM-TrainingDay','DM-Tritium',
  ],
  'Capture the Flag': [
    'CTF-Avaris','CTF-BridgeOfFate','CTF-Chrome','CTF-Citadel','CTF-Colossus',
    'CTF-December','CTF-Face3','CTF-FacingWorlds','CTF-February',
    'CTF-Geothermal','CTF-Grassyknoll','CTF-Grendelkeep','CTF-HallOfGiants',
    'CTF-January','CTF-LostFaith','CTF-Magma','CTF-Maul','CTF-MoonDragon',
    'CTF-Orbital2','CTF-Polaris','CTF-TitanPass',
  ],
  'Onslaught': [
    'ONS-Adara','ONS-ArcticStronghold','ONS-Crossfire','ONS-Dawn','ONS-Dria',
    'ONS-FrostBite','ONS-Primeval','ONS-Severance','ONS-Torlan',
    'ONS-Urban','ONS-Wellspring',
  ],
  'Assault': [
    'AS-Convoy','AS-FallenCity','AS-Glacier','AS-Junkyard',
    'AS-MotherShip','AS-RobotFactory','AS-Siege','AS-Vault',
  ],
  'Bombing Run': [
    'BR-Anubis','BR-Bifrost','BR-Canyon','BR-Disclosure',
    'BR-IceFields','BR-Serenity','BR-SkyLine','BR-Slaughterhouse','BR-TwinTombs',
  ],
  'Double Domination': [
    'DOM-Atlantis','DOM-Conduit','DOM-Core','DOM-Junkyard',
    'DOM-OutRigger','DOM-Renascent','DOM-Ruination','DOM-ScorchedEarth',
  ],
}

const Q3A_MAPS = {
  'Deathmatch': [
    'q3dm0','q3dm1','q3dm2','q3dm3','q3dm4','q3dm5','q3dm6','q3dm7',
    'q3dm8','q3dm9','q3dm10','q3dm11','q3dm12','q3dm13','q3dm14',
    'q3dm15','q3dm16','q3dm17','q3dm18','q3dm19',
  ],
  'Tournament': [
    'q3tourney1','q3tourney2','q3tourney3','q3tourney4','q3tourney5','q3tourney6',
  ],
  'Capture the Flag': [
    'q3ctf1','q3ctf2','q3ctf3','q3ctf4',
  ],
}

const MOHAA_MAPS = {
  'Deathmatch': [
    'dm/mohdm1','dm/mohdm2','dm/mohdm3','dm/mohdm4',
    'dm/mohdm5','dm/mohdm6','dm/mohdm7',
  ],
  'Objective': [
    'obj/obj_team1','obj/obj_team2','obj/obj_team3','obj/obj_team4',
  ],
}

const CS16_MAPS = {
  'Defusal (de_)': [
    'de_dust2','de_dust','de_inferno','de_nuke','de_train','de_aztec',
    'de_cbble','de_prodigy','de_airstrip','de_chateau',
    'de_cpl_mill','de_cpl_strike','de_cpl_fire','de_russka','de_tuscan',
    'de_storm','de_survivor','de_torn','de_vegas','de_vertigo','de_piranesi',
  ],
  'Hostage (cs_)': [
    'cs_office','cs_italy','cs_assault','cs_militia','cs_747',
    'cs_backalley','cs_siege','cs_havana','cs_estate','cs_thunder',
  ],
  'Assassination (as_)': [
    'as_oilrig',
  ],
}

const isUT2004 = (eggName) => {
  if (!eggName) return false
  const l = eggName.toLowerCase()
  return l.includes('ut2004') || l.includes('unreal tournament 2004')
}

const isQ3A = (eggName) => {
  if (!eggName) return false
  const l = eggName.toLowerCase()
  return l.includes('quake 3') || l.includes('quake3') || l.includes('q3a')
}

const isMOHAA = (eggName) => {
  if (!eggName) return false
  return eggName.toLowerCase().includes('mohaa') || eggName.toLowerCase().includes('medal of honor')
}

const isCS16 = (eggName) => {
  if (!eggName) return false
  const l = eggName.toLowerCase()
  return l.includes('rehlds') || l.includes('hlds') || l.includes('goldsrc')
      || l.includes('cs 1.6') || l.includes('cs1.6') || l.includes('cstrike')
      || l.includes('counter-strike') || l.includes('half-life')
}

/* Per-game map-config voor de quick-actions op de server-card. Elk heeft:
   - maps: gegroepeerde dropdown-opties
   - cmd(m): commando om naar die map te wisselen
   - restartCmd (optioneel): commando om huidige map/ronde te herstarten */
const getMapList = (eggName) => {
  if (isUT2004(eggName)) return { maps: UT2004_MAPS, cmd: (m) => `ServerTravel ${m}`, restartCmd: 'RestartMap' }
  if (isQ3A(eggName))    return { maps: Q3A_MAPS,    cmd: (m) => `map ${m}`,          restartCmd: 'map_restart' }
  if (isMOHAA(eggName))  return { maps: MOHAA_MAPS,  cmd: (m) => `map ${m}`,          restartCmd: 'restart' }
  if (isCS16(eggName))   return { maps: CS16_MAPS,   cmd: (m) => `changelevel ${m}`,  restartCmd: 'mp_restartgame 1' }
  return null
}

const formatBytes = (bytes) => {
  if (!bytes) return '0 B'
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(0)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

const formatUptime = (ms) => {
  if (!ms) return '–'
  const s = Math.floor(ms / 1000)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (h > 0) return `${h}u ${m}m`
  return `${m}m ${s % 60}s`
}

const STATUS_CONFIG = {
  running:  { color: 'bg-green-500',  label: 'Online',    icon: Wifi },
  starting: { color: 'bg-yellow-500', label: 'Starting',  icon: Clock },
  stopping: { color: 'bg-orange-500', label: 'Stopping',  icon: Clock },
  offline:  { color: 'bg-red-500',    label: 'Offline',   icon: WifiOff },
  unknown:  { color: 'bg-gray-500',   label: 'Onbekend',  icon: Server },
}

function copyToClipboard(text) {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text)
  }
  // Fallback for HTTP (non-secure) contexts
  const el = document.createElement('textarea')
  el.value = text
  el.style.cssText = 'position:fixed;top:-9999px;left:-9999px'
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
  return Promise.resolve()
}

function PterodactylServerCard({ server, isAdmin, onPowerAction, onRename, onToggleVisibility }) {
  const [loadingAction, setLoadingAction] = useState(null)
  const [copied, setCopied] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [consoleOpen, setConsoleOpen] = useState(false)
  const [selectedMap, setSelectedMap] = useState('')
  const [customCmd, setCustomCmd] = useState('')
  const [cmdResult, setCmdResult] = useState(null)
  const [sendingCmd, setSendingCmd] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(server.name)
  const inputRef = useRef(null)

  const startEdit = () => {
    setEditValue(server.name)
    setEditing(true)
    setTimeout(() => inputRef.current?.select(), 0)
  }

  const cancelEdit = () => {
    setEditing(false)
    setEditValue(server.name)
  }

  const saveEdit = async () => {
    const trimmed = editValue.trim()
    if (!trimmed || trimmed === server.name) { cancelEdit(); return }
    await onRename(server.identifier, trimmed)
    setEditing(false)
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter') saveEdit()
    if (e.key === 'Escape') cancelEdit()
  }

  const cfg = STATUS_CONFIG[server.status] || STATUS_CONFIG.unknown
  const StatusIcon = cfg.icon
  const hidePort = server.name?.toLowerCase().includes('tetrinet')
  const address = server.ip
    ? (server.port && !hidePort ? `${server.ip}:${server.port}` : server.ip)
    : null

  const copyAddress = () => {
    if (!address) return
    copyToClipboard(address)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handlePower = async (signal) => {
    setLoadingAction(signal)
    try {
      await onPowerAction(server.identifier, signal)
    } finally {
      setLoadingAction(null)
    }
  }

  const sendConsoleCommand = async (command) => {
    setSendingCmd(true)
    setCmdResult(null)
    try {
      await pterodactylAPI.sendCommand(server.identifier, command)
      setCmdResult({ ok: true, msg: `Verstuurd: ${command}` })
    } catch (err) {
      setCmdResult({ ok: false, msg: err.response?.data?.error || 'Versturen mislukt' })
    } finally {
      setSendingCmd(false)
      setTimeout(() => setCmdResult(null), 4000)
    }
  }

  const gameMapConfig = getMapList(server.egg_name)

  const handleMapChange = () => {
    if (!selectedMap || !gameMapConfig) return
    sendConsoleCommand(gameMapConfig.cmd(selectedMap))
    setSelectedMap('')
  }

  const handleCustomCmd = (e) => {
    e.preventDefault()
    if (!customCmd.trim()) return
    sendConsoleCommand(customCmd.trim())
    setCustomCmd('')
  }

  const res = server.resources

  return (
    <Card className="group border-border hover:border-neon-blue/40 transition-colors flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-base">
              {editing ? (
                <div className="flex items-center gap-1 flex-1">
                  <input
                    ref={inputRef}
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onKeyDown={onKeyDown}
                    onBlur={saveEdit}
                    className="bg-muted border border-neon-blue/50 rounded px-2 py-0.5 text-sm font-semibold w-full focus:outline-none focus:border-neon-blue"
                    maxLength={64}
                  />
                  <button onClick={cancelEdit} className="text-muted-foreground hover:text-foreground shrink-0">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <span>{server.name}</span>
                  {server.suspended && <Lock className="h-3 w-3 text-muted-foreground" />}
                  {isAdmin && (
                    <button onClick={startEdit} className="text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                </>
              )}
            </CardTitle>
            {server.description && (
              <CardDescription className="text-xs mt-0.5 truncate">{server.description}</CardDescription>
            )}
            <p className="text-xs text-muted-foreground mt-1">{server.node}</p>
          </div>
          <Badge className={`${cfg.color} text-white flex items-center gap-1 shrink-0 text-xs`}>
            <StatusIcon className="h-3 w-3" />
            {cfg.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pt-0 flex flex-col flex-1">
        <div className="space-y-3">
        {/* Address */}
        {address && (
          <div className="flex items-center justify-between p-2 bg-muted/40 rounded text-sm">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Globe className="h-3.5 w-3.5" />
              <span className="font-mono text-xs">{address}</span>
            </div>
            <button onClick={copyAddress} className="text-muted-foreground hover:text-foreground transition-colors">
              {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
        )}

        {/* Players + Map (when running and gamedig data available) */}
        {server.status === 'running' && server.max_players != null && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Users className="h-3.5 w-3.5" />
                <span>Spelers</span>
              </div>
              <span className="font-mono font-semibold">
                {server.current_players ?? 0}
                <span className="text-muted-foreground font-normal"> / {server.max_players}</span>
              </span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(100, ((server.current_players ?? 0) / server.max_players) * 100)}%`,
                  backgroundColor: (server.current_players ?? 0) >= server.max_players
                    ? '#ef4444'
                    : (server.current_players ?? 0) >= server.max_players * 0.75
                      ? '#f59e0b'
                      : '#22c55e'
                }}
              />
            </div>
          </div>
        )}

        {server.status === 'running' && server.map_name && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Map className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate font-mono text-xs">{server.map_name}</span>
          </div>
        )}

        {/* Resources (when running) */}
        {server.status === 'running' && res && (
          <div>
            <button
              onClick={() => setExpanded(e => !e)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground w-full"
            >
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              Systeeminfo
            </button>
            {expanded && (() => {
              const cpuLimit = server.limits?.cpu ?? 0
              const memLimitBytes = (server.limits?.memory ?? 0) * 1024 * 1024
              const cpuPct = cpuLimit > 0 ? Math.min(100, (res.cpu_absolute / cpuLimit) * 100) : null
              const memPct = memLimitBytes > 0 ? Math.min(100, (res.memory_bytes / memLimitBytes) * 100) : null
              const barColor = (pct) => pct > 85 ? 'bg-red-500' : pct > 60 ? 'bg-yellow-500' : 'bg-neon-green'
              return (
                <div className="mt-2 space-y-2 text-xs">
                  {/* CPU */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Cpu className="h-3.5 w-3.5 text-neon-blue shrink-0" />
                      <span className="text-muted-foreground">CPU</span>
                      <span className="font-mono ml-auto">
                        {cpuLimit > 0
                          ? `${cpuPct.toFixed(0)}%`
                          : `${(res.cpu_absolute / 100).toFixed(1)} cores`}
                      </span>
                    </div>
                    {cpuPct != null && (
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${barColor(cpuPct)}`} style={{ width: `${cpuPct}%` }} />
                      </div>
                    )}
                  </div>
                  {/* RAM */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <MemoryStick className="h-3.5 w-3.5 text-neon-purple shrink-0" />
                      <span className="text-muted-foreground">RAM</span>
                      <span className="font-mono ml-auto">
                        {memLimitBytes > 0
                          ? `${formatBytes(res.memory_bytes)} / ${formatBytes(memLimitBytes)}`
                          : formatBytes(res.memory_bytes)}
                      </span>
                    </div>
                    {memPct != null && (
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${barColor(memPct)}`} style={{ width: `${memPct}%` }} />
                      </div>
                    )}
                  </div>
                  {/* Uptime */}
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-neon-green shrink-0" />
                    <span className="text-muted-foreground">Uptime</span>
                    <span className="font-mono ml-auto">{formatUptime(res.uptime)}</span>
                  </div>
                </div>
              )
            })()}
          </div>
        )}
        </div>
        <div className="flex-1" />

        {/* Power buttons (admin) */}
        {isAdmin && !server.suspended && (
          <div className="flex gap-1.5 mt-3">
            {server.status === 'offline' ? (
              <Button
                size="sm"
                className="flex-1 bg-green-600 hover:bg-green-500 text-white"
                onClick={() => handlePower('start')}
                disabled={!!loadingAction}
              >
                <Power className="h-3.5 w-3.5 mr-1" />
                {loadingAction === 'start' ? 'Starting...' : 'Start'}
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                  onClick={() => handlePower('restart')}
                  disabled={!!loadingAction}
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1" />
                  {loadingAction === 'restart' ? '...' : 'Restart'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={() => handlePower('stop')}
                  disabled={!!loadingAction}
                >
                  <Square className="h-3.5 w-3.5 mr-1" />
                  {loadingAction === 'stop' ? '...' : 'Stop'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-700/50 text-red-600 hover:bg-red-700/10 px-2"
                  onClick={() => handlePower('kill')}
                  disabled={!!loadingAction}
                  title="Force kill"
                >
                  <Zap className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </div>
        )}

        {/* Visibility toggle (admin only). Verborgen server → deelnemers
            zien 'm niet op /game-servers, admin ziet 'm met een grijze
            "verborgen"-badge. */}
        {isAdmin && (
          <Button
            size="sm"
            variant="outline"
            className={`w-full mt-3 mb-1 ${server.is_hidden ? 'border-muted-foreground/40 text-muted-foreground' : 'border-live/40 text-live'}`}
            onClick={() => onToggleVisibility(server.identifier, server.is_hidden)}
          >
            {server.is_hidden
              ? <><EyeOff className="h-3.5 w-3.5 mr-1.5" />Verborgen — klik om te tonen</>
              : <><Eye    className="h-3.5 w-3.5 mr-1.5" />Zichtbaar — klik om te verbergen</>
            }
          </Button>
        )}

        {/* Connect button (non-admin, server running) */}
        {!isAdmin && server.status === 'running' && address && (
          <Button
            size="sm"
            variant="outline"
            className="w-full mt-3 border-neon-blue/30 text-neon-blue hover:bg-neon-blue/10 hover:border-neon-blue/60"
            onClick={copyAddress}
          >
            {copied
              ? <><Check className="h-3.5 w-3.5 mr-1.5 text-green-400" /><span className="text-green-400">Gekopieerd!</span></>
              : <><Copy className="h-3.5 w-3.5 mr-1.5" />Kopieer adres</>
            }
          </Button>
        )}

        {/* Volledige commando-page in nieuwe tab — snellere en volledigere
            variant van de inline console-dropdown hieronder. */}
        {isAdmin && server.status === 'running' && (
          <Button
            variant="outline"
            size="sm"
            className="w-full mb-1"
            asChild
          >
            <a href={`/game-console?server=${server.identifier}`} target="_blank" rel="noopener noreferrer">
              <Zap className="h-3.5 w-3.5 mr-1.5 text-acid" />
              Commando's
            </a>
          </Button>
        )}

        {/* Console commands (admin, server running) — beknopte inline versie */}
        {isAdmin && server.status === 'running' && (
          <div className="mt-2">
            <button
              onClick={() => setConsoleOpen(o => !o)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground w-full"
            >
              {consoleOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              <Terminal className="h-3 w-3 ml-0.5" />
              Console
            </button>
            {consoleOpen && (
              <div className="mt-2 space-y-2">
                {/* Map change dropdown */}
                {gameMapConfig && (
                  <div className="flex gap-1">
                    <select
                      value={selectedMap}
                      onChange={e => setSelectedMap(e.target.value)}
                      className="flex-1 bg-muted border border-border rounded px-2 py-1 text-xs focus:outline-none focus:border-neon-blue/50"
                    >
                      <option value="">— Kies map —</option>
                      {Object.entries(gameMapConfig.maps).map(([group, maps]) => (
                        <optgroup key={group} label={group}>
                          {maps.map(m => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      className="px-2 bg-neon-blue/20 hover:bg-neon-blue/30 border border-neon-blue/40 text-neon-blue"
                      onClick={handleMapChange}
                      disabled={!selectedMap || sendingCmd}
                      title="Verander map"
                    >
                      <Map className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}

                {/* Restart map — per-game commando (UT2004: RestartMap,
                    Q3A: map_restart, MOHAA: restart, CS 1.6: mp_restartgame 1) */}
                {gameMapConfig?.restartCmd && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full text-xs border-border"
                    onClick={() => sendConsoleCommand(gameMapConfig.restartCmd)}
                    disabled={sendingCmd}
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Restart map
                  </Button>
                )}

                {/* Free-text command */}
                <form onSubmit={handleCustomCmd} className="flex gap-1">
                  <input
                    value={customCmd}
                    onChange={e => setCustomCmd(e.target.value)}
                    placeholder="Commando..."
                    className="flex-1 bg-muted border border-border rounded px-2 py-1 text-xs font-mono focus:outline-none focus:border-neon-blue/50"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    className="px-2 bg-muted hover:bg-muted/80 border border-border"
                    disabled={!customCmd.trim() || sendingCmd}
                    title="Verstuur"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                </form>

                {/* Result feedback */}
                {cmdResult && (
                  <p className={`text-xs ${cmdResult.ok ? 'text-green-400' : 'text-red-400'}`}>
                    {cmdResult.msg}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default function GameServersPage() {
  // Session-cache — bij het opnieuw openen van de pagina direct de vorige
  // stand tonen, dan pas op de achtergrond verversen. Zo geen witte pagina.
  const cacheLees = (k) => {
    try { const r = sessionStorage.getItem(k); return r ? JSON.parse(r) : null } catch { return null }
  }
  const cacheSchrijf = (k, v) => {
    try { sessionStorage.setItem(k, JSON.stringify(v)) } catch {}
  }

  const [ptServers, setPtServers]         = useState(() => cacheLees('gs-pt')     || [])
  const [manualServers, setManualServers] = useState(() => cacheLees('gs-manual') || [])
  const [ptLoading, setPtLoading]         = useState(() => (cacheLees('gs-pt')     || []).length === 0)
  const [ptEnriching, setPtEnriching]     = useState(false)
  const [manualLoading, setManualLoading] = useState(() => (cacheLees('gs-manual') || []).length === 0)
  const [error, setError] = useState(null)
  const [user, setUser] = useState(null)
  const [actionMsg, setActionMsg] = useState(null)

  /** Pterodactyl — cascade: eerst lichte basic-cards, dan enrichment
      met resources + player-count zodra die binnenkomen. */
  const fetchPterodactyl = useCallback(async () => {
    setError(null)
    // 1) Basic-endpoint (snel, zonder resources/gamedig) → cards renderen.
    try {
      const basicRes = await pterodactylAPI.getServersBasic()
      const basic = basicRes.data?.servers || []
      // Merge: bestaande enriched data behouden waar mogelijk, anders basic.
      setPtServers(prev => basic.map(b => {
        const oud = prev.find(p => p.identifier === b.identifier)
        return oud && !oud._basic ? { ...b, ...oud, _basic: false } : b
      }))
      setPtLoading(false)
    } catch (e) {
      if (!ptServers.length) setError('Kan Pterodactyl-servers niet laden')
    }
    // 2) Volle endpoint met resources + gamedig — vult stats aan.
    setPtEnriching(true)
    try {
      const fullRes = await pterodactylAPI.getServers()
      const full = fullRes.data?.servers || []
      setPtServers(full)
      cacheSchrijf('gs-pt', full)
    } catch {} finally {
      setPtEnriching(false)
    }
  }, [ptServers.length])

  const fetchManual = useCallback(async () => {
    try {
      const r = await gameServersAPI.getServers()
      const lijst = r.data?.servers || []
      setManualServers(lijst)
      cacheSchrijf('gs-manual', lijst)
    } catch {
      // niet fataal — pterodactyl kan wel werken
    } finally {
      setManualLoading(false)
    }
  }, [])

  const fetchAll = useCallback(() => {
    // Onafhankelijk — de snelste van de twee toont eerst.
    fetchPterodactyl()
    fetchManual()
  }, [fetchPterodactyl, fetchManual])

  useEffect(() => {
    authAPI.getCurrentUser().then(r => setUser(r.data.user)).catch(() => {})
    fetchAll()
    const interval = setInterval(fetchAll, 30000)
    return () => clearInterval(interval)
  }, [fetchAll])

  const loading = ptLoading || manualLoading

  const handleRename = async (identifier, displayName) => {
    try {
      await api.put(`/pterodactyl/servers/${identifier}/display-name`, { display_name: displayName })
      setPtServers(prev => prev.map(s =>
        s.identifier === identifier ? { ...s, name: displayName } : s
      ))
    } catch (err) {
      setError(err.response?.data?.error || 'Naam opslaan mislukt')
      setTimeout(() => setError(null), 4000)
    }
  }

  const handleToggleVisibility = async (identifier, current) => {
    try {
      await pterodactylAPI.setVisibility(identifier, !current)
      setPtServers(prev => prev.map(s =>
        s.identifier === identifier ? { ...s, is_hidden: !current } : s
      ))
    } catch (err) {
      setError(err.response?.data?.error || 'Zichtbaarheid wijzigen mislukt')
      setTimeout(() => setError(null), 4000)
    }
  }

  const handlePowerAction = async (identifier, signal) => {
    try {
      await api.post(`/pterodactyl/servers/${identifier}/power`, { signal })
      const labels = { start: 'gestart', stop: 'gestopt', restart: 'herstart', kill: 'geforceerd gestopt' }
      setActionMsg(`Server ${labels[signal] || signal}`)
      setTimeout(() => setActionMsg(null), 3000)
      // Refresh status after short delay
      setTimeout(fetchAll, 2000)
    } catch (err) {
      setError(err.response?.data?.error || 'Power actie mislukt')
      setTimeout(() => setError(null), 4000)
    }
  }

  const isAdmin = ['admin','superadmin'].includes(user?.role)

  // Dedup: als een game_servers-rij dezelfde poort/naam heeft als een
  // Pterodactyl-server, tonen we hem alleen onder Pterodactyl. Anders krijg
  // je hem dubbel — één keer "starting" (Pterodactyl-status) en één keer
  // "online" (game_servers-status), en dat is verwarrend.
  const manualDeduped = useMemo(() => {
    const ptKeys = new Set()
    for (const s of ptServers) {
      if (s.name)          ptKeys.add('n:' + s.name.toLowerCase())
      if (s.port)          ptKeys.add('p:' + s.port)
      if (s.identifier)    ptKeys.add('i:' + s.identifier)
    }
    return manualServers.filter(m => {
      if (m.name && ptKeys.has('n:' + m.name.toLowerCase())) return false
      if (m.port && ptKeys.has('p:' + m.port)) return false
      return true
    })
  }, [ptServers, manualServers])

  const hasServers = ptServers.length > 0 || manualDeduped.length > 0

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-neon-blue to-neon-purple flex items-center justify-center">
            <Server className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Game Servers</h1>
            <p className="text-muted-foreground text-sm">
              {ptServers.length} Pterodactyl server{ptServers.length !== 1 ? 's' : ''}
              {manualDeduped.length > 0 && ` · ${manualDeduped.length} overig`}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading || ptEnriching ? 'animate-spin' : ''}`} />
          {ptEnriching && !loading ? 'Details ophalen…' : 'Vernieuwen'}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {actionMsg && (
        <Alert className="mb-4 border-green-500/30 bg-green-500/10">
          <AlertDescription className="text-green-400">{actionMsg}</AlertDescription>
        </Alert>
      )}

      {loading && !hasServers && (
        <div className="flex items-center justify-center py-20">
          <Server className="h-8 w-8 animate-pulse text-muted-foreground" />
        </div>
      )}

      {!loading && !hasServers && (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <Server className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Geen game servers gevonden</p>
          </CardContent>
        </Card>
      )}

      {/* Pterodactyl servers */}
      {ptServers.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Zap className="h-5 w-5 text-neon-blue" />
            Pterodactyl
            <Badge variant="secondary">{ptServers.length}</Badge>
            {isAdmin && (
              <span className="text-xs text-muted-foreground font-normal ml-1">
                · beheer via start/stop knoppen
              </span>
            )}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ptServers.map(server => (
              <PterodactylServerCard
                key={server.identifier}
                server={server}
                isAdmin={isAdmin}
                onPowerAction={handlePowerAction}
                onRename={handleRename}
                onToggleVisibility={handleToggleVisibility}
              />
            ))}
          </div>
        </div>
      )}

      {/* Manual servers — Pterodactyl-servers gededupliceerd zodat een server
          niet dubbel verschijnt (één keer Pterodactyl, één keer overig). */}
      {manualDeduped.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Server className="h-5 w-5 text-muted-foreground" />
            Overige servers
            <Badge variant="secondary">{manualDeduped.length}</Badge>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {manualDeduped.map(server => (
              <Card key={server.id} className="border-border flex flex-col">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        {server.name}
                        {server.password_protected ? <Lock className="h-3 w-3 text-muted-foreground" /> : null}
                      </CardTitle>
                      {server.description && <CardDescription className="text-xs mt-0.5">{server.description}</CardDescription>}
                    </div>
                    <Badge className={`${server.status === 'online' ? 'bg-green-500' : server.status === 'maintenance' ? 'bg-yellow-500' : 'bg-red-500'} text-white text-xs`}>
                      {server.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 flex flex-col flex-1">
                  <div className="space-y-2">
                  {server.ip_address && (
                    <p className="font-mono text-xs text-muted-foreground">
                      {server.ip_address}{server.port ? `:${server.port}` : ''}
                    </p>
                  )}
                  {server.max_players != null && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Users className="h-3.5 w-3.5" />
                          <span>Spelers</span>
                        </div>
                        <span className="font-mono font-semibold">
                          {server.current_players || 0}
                          <span className="text-muted-foreground font-normal"> / {server.max_players}</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${Math.min(100, ((server.current_players || 0) / server.max_players) * 100)}%`,
                            backgroundColor: (server.current_players || 0) >= server.max_players
                              ? '#ef4444'
                              : (server.current_players || 0) >= server.max_players * 0.75
                                ? '#f59e0b'
                                : '#22c55e'
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {server.map_name && (
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Map className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate font-mono text-xs">{server.map_name}</span>
                    </div>
                  )}
                  </div>
                  <div className="flex-1" />
                  {server.status === 'online' && server.ip_address && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full mt-3 border-neon-blue/30 text-neon-blue hover:bg-neon-blue/10 hover:border-neon-blue/60"
                      onClick={() => {
                        const addr = server.port ? `${server.ip_address}:${server.port}` : server.ip_address
                        copyToClipboard(addr)
                      }}
                    >
                      <Copy className="h-3.5 w-3.5 mr-1.5" />
                      Kopieer adres
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
